/**
 * <ss-faq-search> — topic filter + text search over a list of <details> answers.
 *
 * Everything here is an enhancement. The section server-renders every question
 * as a native <details>, so with no JavaScript the page is still a complete,
 * keyboard-operable FAQ — the search field, the topic row and the expand-all
 * button are the only things that need this file, and CSS hides all three
 * until the element upgrades.
 *
 * Two details worth knowing:
 *   - Each row carries its question and answer pre-flattened in data-text, so
 *     a keystroke is a string compare rather than a DOM walk.
 *   - The visible numbering is a CSS counter. Hidden rows do not increment a
 *     counter, so filtering renumbers the list without any bookkeeping here.
 */
class SSFaqSearch extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-ss-faq-list]');
    if (!this.list) return;

    this.items = Array.from(this.querySelectorAll('.ss-faqs__item'));
    if (this.items.length === 0) return;

    this.cats = Array.from(this.querySelectorAll('.ss-faqs__cat'));
    this.input = this.querySelector('[data-ss-faq-query]');
    this.count = this.querySelector('[data-ss-faq-count]');
    /* NOT this.title: HTMLElement.title is a built-in string property, so
       assigning an element to it coerces to "[object HTMLHeadingElement]" and
       the next .textContent write throws. Same trap for .id, .lang, .dir. */
    this.asideTitle = this.querySelector('[data-ss-faq-title]');
    this.blurb = this.querySelector('[data-ss-faq-blurb]');
    this.expand = this.querySelector('[data-ss-faq-expand]');
    this.empty = this.querySelector('[data-ss-faq-empty]');
    this.emptyTitle = this.querySelector('[data-ss-faq-empty-title]');
    this.clear = this.querySelector('[data-ss-faq-clear]');

    this.emptyTemplate = this.emptyTitle ? this.emptyTitle.textContent : '';
    this.cat = '';

    this.onInput = this.onInput.bind(this);
    this.onCat = this.onCat.bind(this);
    this.onExpand = this.onExpand.bind(this);
    this.onClear = this.onClear.bind(this);

    this.input?.addEventListener('input', this.onInput);
    this.input?.addEventListener('search', this.onInput);
    this.cats.forEach((b) => b.addEventListener('click', this.onCat));
    this.expand?.addEventListener('click', this.onExpand);
    this.clear?.addEventListener('click', this.onClear);

    this.apply();
  }

  disconnectedCallback() {
    clearTimeout(this.countTimer);
    this.input?.removeEventListener('input', this.onInput);
    this.input?.removeEventListener('search', this.onInput);
    this.cats?.forEach((b) => b.removeEventListener('click', this.onCat));
    this.expand?.removeEventListener('click', this.onExpand);
    this.clear?.removeEventListener('click', this.onClear);
  }

  onInput() {
    this.apply();
  }

  onCat(event) {
    const button = event.currentTarget;
    this.cat = button.dataset.cat || '';

    for (const b of this.cats) {
      b.setAttribute('aria-pressed', b === button ? 'true' : 'false');
    }
    if (this.asideTitle && button.dataset.title) {
      this.asideTitle.textContent = button.dataset.title;
    }
    if (this.blurb) this.blurb.textContent = button.dataset.blurb || '';

    this.apply();
  }

  onClear() {
    if (this.input) this.input.value = '';
    this.cat = '';
    const all = this.cats.find((b) => !b.dataset.cat);
    if (all) all.click();
    else this.apply();
    this.input?.focus();
  }

  onExpand() {
    const visible = this.items.filter((i) => !i.hidden);
    const opening = !visible.every((i) => i.open);
    for (const item of visible) item.open = opening;

    this.expand.textContent = opening
      ? this.expand.dataset.collapse
      : this.expand.dataset.expand;
    this.expand.setAttribute('aria-expanded', String(opening));
  }

  apply() {
    /* Matched lowercase, echoed back as typed — quoting someone's search back
       at them in the wrong case reads like a transcription error. */
    const typed = (this.input?.value || '').trim();
    const query = typed.toLowerCase();
    let shown = 0;
    let last = null;

    for (const item of this.items) {
      const catOk = !this.cat || (item.dataset.cat || '') === this.cat;
      const textOk = !query || (item.dataset.text || '').includes(query);
      const match = catOk && textOk;

      item.hidden = !match;
      /* A hidden row still counts as :last-of-type, so the rule that drops the
         final divider has to be moved to the last row actually on screen. */
      item.classList.remove('ss-faqs__item--last');
      if (match) {
        shown += 1;
        last = item;
      }
    }
    last?.classList.add('ss-faqs__item--last');

    /* The count is a polite live region, so writing it on every keystroke would
       make a screen reader recite the tally letter by letter. The list itself
       filters immediately; only the announcement waits for a pause in typing. */
    if (this.count) {
      const template = shown === 1 ? this.count.dataset.one : this.count.dataset.many;
      const text = (template || '')
        .replace('[n]', String(shown))
        .replace('[total]', this.count.dataset.total || String(this.items.length));
      clearTimeout(this.countTimer);
      this.countTimer = setTimeout(() => {
        this.count.textContent = text;
      }, 300);
    }

    if (this.empty) {
      this.empty.hidden = shown !== 0;
      if (shown === 0 && this.emptyTitle) {
        this.emptyTitle.textContent = this.emptyTemplate.replace('[q]', typed);
      }
    }

    /* The label is derived from what is actually open rather than tracked in
       state, so it stays honest when the filter changes what "all" means — and
       nothing the visitor has already opened gets closed underneath them. */
    if (this.expand) {
      this.expand.hidden = shown === 0;
      const visible = this.items.filter((i) => !i.hidden);
      const allOpen = visible.length > 0 && visible.every((i) => i.open);
      this.expand.textContent = allOpen
        ? this.expand.dataset.collapse
        : this.expand.dataset.expand;
      this.expand.setAttribute('aria-expanded', String(allOpen));
    }
  }
}

if (!customElements.get('ss-faq-search')) {
  customElements.define('ss-faq-search', SSFaqSearch);
}
